import { Injectable, inject } from "@angular/core";
import {
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  QueryConstraint,
  Timestamp,
  Firestore,
  runTransaction,
  increment,
  getAggregateFromServer,
  sum as sumAgg,
} from "@angular/fire/firestore";
import { FirestoreService } from "./firestore.service";
import {
  Bill,
  Client,
  ClientSnapshot,
  COL,
} from "./types";
import { ClientsService } from "./clients.service";
import { buildClientSnapshot } from "./legacy-mappers";

export interface BillFilters {
  /** @deprecated Ignored — queries are firm-wide (see Firestore rules). */
  creatorUid?: string;
  clientId?: string;
  /** "TODAS" or numeric (0/1/2). String to match the legacy form. */
  state?: string | number;
  start?: Date | null;
  end?: Date | null;
  /** Optional category filter (denormalized via client.category). */
  category?: string;
  limit?: number;
  /**
   * Cursor for pagination. Pass the value of the field used in `orderBy`
   * (here `createdAt`) of the last bill of the previous page. Avoids the
   * extra `getDoc` that `startAfterId` used to incur.
   */
  startAfter?: Timestamp | Date | null;
}

@Injectable({ providedIn: "root" })
export class BillsService {
  private readonly fs = inject(FirestoreService);
  private readonly db = inject(Firestore);
  private readonly clients = inject(ClientsService);

  // ---- Reads ---------------------------------------------------------------

  async findBills(filters: BillFilters): Promise<Bill[]> {
    const colRef = this.fs.col<Bill>(COL.bills);
    const constraints = this.buildBillConstraints(filters);
    const snap = await getDocs(query(colRef, ...constraints));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Bill));
    return this.hydrateNaturalPersonSnapshotNames(list);
  }

  /**
   * Old bills embed `client` without `name` (only companyName + lastname).
   * For natural persons, merge the live `clients/{id}` doc so lists show nombre + apellidos.
   */
  private snapshotNeedsNaturalPersonName(s: ClientSnapshot): boolean {
    if ((s.personType ?? 1) === 2) return false;
    return !String(s.name ?? "").trim();
  }

  private async hydrateNaturalPersonSnapshotNames(bills: Bill[]): Promise<Bill[]> {
    const ids = new Set<string>();
    for (const b of bills) {
      if (this.snapshotNeedsNaturalPersonName(b.client)) {
        ids.add(b.clientId);
      }
    }
    if (!ids.size) return bills;

    const byId = new Map<string, Client>();
    await Promise.all(
      [...ids].map(async (clientId) => {
        try {
          const row = await this.clients.getById(clientId);
          if (row) byId.set(clientId, row);
        } catch {
          /* missing / permission */
        }
      })
    );

    return bills.map((b) => {
      if (!this.snapshotNeedsNaturalPersonName(b.client)) return b;
      const row = byId.get(b.clientId);
      if (!row) return b;
      return {
        ...b,
        client: {
          ...b.client,
          name: row.name ?? b.client.name,
          personType: row.personType ?? b.client.personType,
        },
      };
    });
  }

  /**
   * Sum of `balance` across the bills matching the filters.
   *
   * Uses Firestore's aggregation API: a single billable read regardless of
   * how many documents match. Replaces the old O(N) `getDocs` + reduce.
   */
  async sumBalance(filters: BillFilters): Promise<number> {
    const colRef = this.fs.col<Bill>(COL.bills);
    const constraints = this.buildWhereConstraints(filters);
    const agg = await getAggregateFromServer(query(colRef, ...constraints), {
      total: sumAgg("balance"),
    });
    return Number(agg.data().total ?? 0);
  }

  /**
   * Equality + range filters only — no order/cursor/limit. Used by both
   * list queries (which add their own orderBy/limit/cursor) and aggregation
   * queries (which can't take any of those).
   */
  private buildWhereConstraints(filters: BillFilters): QueryConstraint[] {
    const c: QueryConstraint[] = [];
    if (filters.creatorUid) c.push(where("creatorUid", "==", filters.creatorUid));
    if (filters.clientId) c.push(where("clientId", "==", filters.clientId));

    if (filters.state !== undefined && filters.state !== null && String(filters.state) !== "TODAS") {
      c.push(where("paid", "==", Number(filters.state)));
    }

    if (filters.category && filters.category !== "TODAS") {
      c.push(where("client.category", "==", filters.category));
    }

    if (filters.start) c.push(where("createdAt", ">=", Timestamp.fromDate(filters.start)));
    if (filters.end) c.push(where("createdAt", "<=", Timestamp.fromDate(filters.end)));

    return c;
  }

  private buildBillConstraints(filters: BillFilters): QueryConstraint[] {
    const c = this.buildWhereConstraints(filters);
    c.push(orderBy("createdAt", "desc"));

    if (filters.startAfter) {
      const ts =
        filters.startAfter instanceof Date
          ? Timestamp.fromDate(filters.startAfter)
          : filters.startAfter;
      c.push(startAfter(ts));
    }

    if (filters.limit) c.push(fsLimit(filters.limit));
    return c;
  }

  // ---- Writes --------------------------------------------------------------

  /**
   * Creates a bill atomically:
   *   - Reads only the counter doc (inside the transaction).
   *   - Writes the bill, updates the counter, increments the client totals
   *     in a single transaction. No extra read-then-write round-trips.
   *
   * `clientSnapshot` SHOULD be provided by the caller (it usually has the
   * full client in NgxS state already). When omitted we fall back to a
   * `getById` read, which is the only client read this method ever makes.
   */
  async createBill(
    input: {
      clientId: string;
      mount: number;
      balance: number;
      notes?: string;
      clientSnapshot?: ClientSnapshot;
    },
    creator: { uid: string; name: string }
  ): Promise<Bill> {
    let snapshot = input.clientSnapshot;
    if (!snapshot) {
      const client = await this.clients.getById(input.clientId);
      if (!client) throw new Error("Cliente no encontrado");
      snapshot = buildClientSnapshot(client);
    }

    const id = doc(this.fs.col(COL.bills)).id;
    const billRef = this.fs.docRef<Bill>(COL.bills, id);
    const counterRef = this.fs.docRef<{ next: number }>(COL.counters, "bills");
    const clientRef = this.fs.docRef<Client>(COL.clients, input.clientId);

    const mount = Number(input.mount);
    const balance = Number(input.balance ?? input.mount);
    const notes = input.notes ?? null;

    const consecutive = await runTransaction(this.db, async (tx) => {
      const counterSnap = await tx.get(counterRef);
      const next = Number(counterSnap.data()?.next ?? 0) + 1;

      tx.set(counterRef as any, { next }, { merge: true });
      tx.set(billRef as any, {
        consecutive: next,
        clientId: input.clientId,
        creatorUid: creator.uid,
        creatorName: creator.name,
        client: snapshot!,
        mount,
        balance,
        paid: 0,
        notes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(clientRef as any, {
        totalBalance: increment(balance),
        unpaidCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      return next;
    });

    // Build the response locally — no post-write read needed. The only
    // unresolved fields are the server timestamps; we approximate with the
    // current client time, which is what every UI in this app shows anyway.
    const now = Timestamp.now();
    return {
      id,
      consecutive,
      clientId: input.clientId,
      creatorUid: creator.uid,
      creatorName: creator.name,
      client: snapshot!,
      mount,
      balance,
      paid: 0,
      notes,
      createdAt: now,
      updatedAt: now,
    };
  }

  async editNotes(id: string, notes: string): Promise<{ id: string; notes: string }> {
    const ref = this.fs.docRef<Bill>(COL.bills, id);
    await updateDoc(ref as any, { notes, updatedAt: serverTimestamp() });
    // Skip the post-write read: callers patch the existing bill in NgxS state
    // with the new notes value.
    return { id, notes };
  }

  /**
   * Marks a bill as annulled. Uses a transaction so the bill update and the
   * client's denormalized totals stay in sync without an extra read on the
   * client doc.
   */
  async annul(id: string): Promise<void> {
    const billRef = this.fs.docRef<Bill>(COL.bills, id);

    await runTransaction(this.db, async (tx) => {
      const snap = await tx.get(billRef);
      if (!snap.exists()) return;
      const bill = snap.data() as Bill;
      if (bill.paid === 2) return; // already annulled

      const wasUnpaid = bill.paid === 0;
      const remaining = Number(bill.balance || 0);
      const clientRef = this.fs.docRef<Client>(COL.clients, bill.clientId);

      tx.update(billRef as any, {
        paid: 2,
        balance: 0,
        updatedAt: serverTimestamp(),
      });
      tx.update(clientRef as any, {
        totalBalance: increment(-remaining),
        unpaidCount: wasUnpaid ? increment(-1) : increment(0),
        updatedAt: serverTimestamp(),
      });
    });
  }
}
